import React, { useEffect, useMemo } from 'react';
import { useState } from 'react';
import { discoverLinkedSourceCatalog, getSheetsFiles } from '../../api/client';

import {
  Box,
  Button,
  CircularProgress,
  Card,
  Divider,
  IconButton,
  Typography,
  Skeleton,
  TextField,
  Stack,
} from '@mui/material';
import { ArrowBack, Folder, InsertDriveFile, InsertDriveFileOutlined, Search } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { getSelectedFiles, updateSelectedFiles } from '../../api/client';
import { useSelector } from 'react-redux';

import { Alert } from '../popupAlerts';
import * as UIAction from '../../redux/actions/ui.actions';

import GSheetLogo from '../../assets/GSheets-logo.svg';
import GSheetIcon from '../../assets/sheet-logo.svg';
import FileUpload from '../../assets/file-upload.svg';

const SheetList = (props) => {
  const entity = useSelector((state) => state.entities.selectedEntity);
  const entityId = useMemo(
    () => (entity.entityType === 'connector' ? entity.id : entity.entityType === 'source' ? entity.tap : entity.target),
    [entity]
  );
  const isGoogleSheets = entityId.includes('sheets');
  const [tree, setTree] = useState([]);
  const [selected, setSelected] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [loadingSelectedFiles, setLoadingSelectedFiles] = useState(false);

  useEffect(() => {
    const fetchSheetsFiles = async () => {
      try {
        const files = await getSheetsFiles(entityId);
        setTree(files);
      } catch (e) {
        Alert({ text: `Something went wrong! ${e.message}`, type: 'error' });
      }
    };

    fetchSheetsFiles();
  }, []);

  useEffect(() => {
    const getFiles = async () => {
      const res = await getSelectedFiles(entityId);
      setSelected(res.files || []);
    };
    getFiles();
  }, []);

  const handleClick = async (file) => {
    setLoading(false);
    setSelected([...selected, file]);
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      await updateSelectedFiles(selected, entityId);
      // Trigger a discover to get the new catalog for the sheet that is selected
      if (entityId.includes("sheets"))
        await discoverLinkedSourceCatalog(entityId);
      Alert({ text: 'Files saved!', type: 'success' });
      UIAction.goBack();
    } catch (e) {
      Alert({ text: 'Something went wrong!', type: 'error' });
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        flex="1"
        maxHeight={'50vh'}
        width="100%"
        overflow="hidden"
        gap={2}
      >
        <Card
          display="flex"
          flexDirection="column"
          alignItems="start"
          elevation={0}
          sx={{
            width: '100%',
            overflowY: 'auto',
            p: 2,
            pt: 0,
            border: ({ palette }) => `1px solid ${palette.common.outline}`,
          }}
        >
          <Box
            display="flex"
            alignItems="flex-start"
            flexDirection="column"
            pb={2}
            style={{ cursor: 'pointer' }}
            position="sticky"
            pt={2.5}
            gap={1.5}
            backgroundColor="common.white"
            top="0"
          >
            <Typography variant="body1">Available {entityId.includes("sheets") ? "Sheets" : "Files"}</Typography>
            <TextField
              id="search"
              type="search"
              variant="standard"
              color="secondary"
              placeholder={entityId.includes("sheets") ? "Search Sheets" : "Search Files"}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              size="small"
              InputProps={{
                endAdornment: <Search sx={{ color: '#ccc', fontSize: '16px' }}></Search>,
              }}
              fullWidth
            />
          </Box>
          {tree.length === 0 && (
            <Stack spacing={1}>
              <Skeleton variant="rectangular" width={280} height={24} />
              <Skeleton variant="rectangular" width={280} height={24} />
              <Skeleton variant="rectangular" width={280} height={24} />
            </Stack>
          )}
          {tree.length > 0 &&
            tree
              .filter((file) => file.name.toLowerCase().includes(term.toLowerCase()))
              .map((file) => (
                <React.Fragment key={file.id}>
                  <Box
                    display="flex"
                    sx={{
                      cursor: 'pointer',
                      height: 'auto',
                      py: 1,
                      px: 0.5,
                      ':hover': {
                        backgroundColor: '#eee',
                      },
                    }}
                    alignItems="center"
                    onClick={() => handleClick(file)}
                  >
                    {isGoogleSheets ? <Box component="img" src={GSheetIcon} alt={ "SpreadSheet Icon"} width="16px" height="16px" /> : <InsertDriveFileOutlined fontSize={'16px'} />}
                    <Typography style={{ marginLeft: 4 }} variant="body1" fontWeight="300">
                      {file.name}
                    </Typography>
                  </Box>
                  <Divider />
                </React.Fragment>
              ))}
        </Card>
        <Card
          display="flex"
          flexDirection="column"
          alignItems="start"
          elevation={0}
          sx={{
            p: 2,
            pt: 2.5,
            width: '80%',
            overflowY: 'auto',
            border: ({ palette }) => `1px solid ${palette.common.outline}`,
          }}
        >
          <Typography variant="body1" pb={2}>
            {entityId.includes("sheets") ? "Selected Sheets" : "Selected Files"}
          </Typography>
          {!selected ? (
            <Stack spacing={1}>
              <Skeleton variant="rectangular" width={200} height={24} />
              <Skeleton variant="rectangular" width={200} height={24} />
            </Stack>
          ) : Array.isArray(selected) && selected.length > 0 ? (
            selected.map((f) => (
              <Box
                key={f.id}
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                onClick={() => setSelected(selected.filter((file) => file.id !== f.id))}
                sx={{
                  cursor: 'pointer',
                  height: 32,
                  p: 0.5,
                  mb: 1,
                  border: ({ palette }) => `1px solid ${palette.common.outline}`,
                  ':hover': {
                    backgroundColor: '#eee',
                  },
                }}
              >
                <Box width={1} display="flex" alignItems='center'>
                  {isGoogleSheets ? <Box component="img" src={GSheetIcon} alt={ "SpreadSheet Icon"} width="16px" height="16px" /> : <InsertDriveFileOutlined fontSize={'16px'} />}
                  <Typography
                    sx={{ ml: 1, width: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    variant="body1"
                    fontWeight="300"
                  >
                    {f.name}
                  </Typography>
                </Box>
                <CloseIcon sx={{ fontSize: '16px' }} />
              </Box>
            ))
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
              height="80%"
              paddingTop="24px"
              gap={3}
            >
              <Box component="img" src={isGoogleSheets ? GSheetLogo : FileUpload} alt={isGoogleSheets ? "GoogleSheet Icon" : "File Upload Icon"} width="40px" height="40px" />
              <Box>
                <Typography sx={{ mb: 1 }} variant="body1">
                  Your selected {entityId.includes("sheets") ? "sheets" : "files"} will appear here.
                </Typography>
                <Typography variant="body2" fontWeight="300" color="text.lightish">
                  You can select one or more {entityId.includes("sheets") ? "sheets" : "files"} to import.
                </Typography>
              </Box>
            </Box>
          )}
        </Card>
      </Box>
      <Box display="flex" justifyContent={'space-between'} py={"16px"} position='sticky' bottom={0} sx={{
        backgroundColor: ({ palette }) => palette.common.white,
      }}>
        <Button variant="outlined" color="text" onClick={() => UIAction.goBack()}>
          Cancel
        </Button>
        <Button
          disabled={(selected && selected.length === 0) || saveLoading}
          onClick={handleSave}
          variant="contained"
          color="accent"
          sx={{ fontSize: '14px', px: 4 }}
          form="app-params-form"
          type="submit"
        >
          {saveLoading && <CircularProgress style={{ width: '16px', height: '16px' }} />}
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default SheetList;
